import { useTranslation } from 'next-i18next'

import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Input from '~/bootcamp/Activities/components/Input'
import { CircleWithSlice } from '~/bootcamp/Activities/Fractions/components/CircleWithSlice/CircleWithSlice'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { RectangleWithSlice } from '~/bootcamp/Activities/Fractions/components/RectangleWithSlice/RectangleWithSlice'
import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress, parseWholeNumber } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'

function FractionDenomInput({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [isRectangle, setIsRectangle] = useState(true)
  const [inputKey, setInputKey] = useState<string>(uuid())

  const [numOfNumerator, setNumOfNumerator] = useState<number>(0)
  const [numOfDenominator, setNumOfDenominator] = useState<number>(2)

  const [taskHistory, setTaskHistory] = useState<number[]>([])
  // A real round can equal the placeholder values, so gate the emit on the generator having run.
  const [ready, setReady] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { minNumerator, maxNumerator, minDenominator, maxDenominator, resetHistory } =
    getRangesByDifficulty(difficultyLevel)

  useEffect(() => {
    setIsRectangle(Math.random() < 0.5)
    setAnswer('')
    const { numerator, denominator } = generateDenominatorNumerator(
      minNumerator,
      maxNumerator,
      minDenominator,
      maxDenominator,
      taskHistory,
    )
    setNumOfNumerator(numerator)
    setNumOfDenominator(denominator)
    setReady(true)
  }, [difficultyLevel, maxDenominator, maxNumerator, minDenominator, minNumerator, taskHistory])

  // The total-parts count is the answer and rides only the guarded channel.
  useEmitInstance(
    onInstanceChange,
    ready ? `\\frac{${numOfNumerator}}{\\square}` : null,
    ready ? `${numOfDenominator}` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = parseWholeNumber(answer) === numOfDenominator
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [numOfNumerator, ...prevState].slice(0, resetHistory)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        {isRectangle ? (
          <RectangleWithSlice className="w-28" denominator={numOfDenominator} isRandomFill numerator={numOfNumerator} />
        ) : (
          <CircleWithSlice numOfDenominator={numOfDenominator} numOfNumerator={numOfNumerator} />
        )}
        <div className="w-6 flex flex-col items-center ml-5">
          {numOfNumerator}
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={4}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionDenomInput
